import { useEffect } from "react";
import type IJitsiMeetExternalApi from "@jitsi/react-sdk/lib/types/IJitsiMeetExternalApi";
import { useCallModerationState } from "@/zustand/CallModerationState";
import {
  JITSI_MODERATOR_ROLE,
  type JitsiParticipantInfo,
  type ModerationStatusChangedEvent,
  type ParticipantJoinedEvent,
  type ParticipantLeftEvent,
  type ParticipantRoleChangedEvent,
  type VideoConferenceJoinedEvent,
} from "./moderationTypes";
import { toBreakoutRooms, toModerationParticipant } from "./moderationUtils";

/**
 * Wires the Jitsi iframe's moderation-relevant events (design doc §3/§7) into
 * `CallModerationState`. Every handler reads `useCallModerationState.getState()` at call time
 * (same pattern as `CallButtons.tsx`'s `startCall`) instead of taking selectors as hook
 * dependencies, so the listeners never go stale across renders.
 *
 * Payload shapes: the breakout-rooms payload is VERIFIED against the stable/jitsi-meet_11031
 * source (see `toBreakoutRooms`); the other event payloads remain unconfirmed against a live
 * conference on our pinned tag. Every handler still treats fields as optional and bails out
 * rather than assuming a shape.
 */
export default function useModerationEvents(
  apiRef: IJitsiMeetExternalApi | null,
): void {
  useEffect(() => {
    if (!apiRef) {
      return;
    }

    // Best-effort reconciliation against the full roster snapshot. `getParticipantsInfo()` is
    // declared as fully untyped `object[]` in the SDK — every field is read defensively.
    function reconcileFromParticipantsInfo() {
      if (typeof apiRef?.getParticipantsInfo !== "function") {
        return;
      }
      let infos: unknown;
      try {
        infos = apiRef.getParticipantsInfo();
      } catch {
        return;
      }
      if (!Array.isArray(infos)) {
        return;
      }
      const { localParticipantId, upsertParticipant } =
        useCallModerationState.getState();
      infos.forEach((raw) => {
        if (!raw || typeof raw !== "object") {
          return;
        }
        const info = raw as JitsiParticipantInfo;
        if (!info.participantId) {
          return;
        }
        // Roster-snapshot only supplies identity (id/displayName). `getParticipantsInfo()`
        // carries NO role/moderator field (verified against stable/jitsi-meet_11031), so we must
        // never read a role from it — moderator status is tracked solely via the
        // `participantRoleChanged` event (and the initial role on `video-conference-joined`).
        upsertParticipant(
          toModerationParticipant(
            { ...info, participantId: info.participantId },
            { isLocal: info.participantId === localParticipantId },
          ),
        );
      });
    }

    function handleVideoConferenceJoined(payload?: VideoConferenceJoinedEvent) {
      const id = payload?.id;
      if (!id) {
        return;
      }
      const { setLocalParticipantId, upsertParticipant, setParticipantRole } =
        useCallModerationState.getState();
      setLocalParticipantId(id);
      upsertParticipant(
        toModerationParticipant(
          { participantId: id, displayName: payload?.displayName },
          { isLocal: true },
        ),
      );
      if (typeof payload?.role === "string") {
        setParticipantRole(id, payload.role === JITSI_MODERATOR_ROLE);
      }
      reconcileFromParticipantsInfo();
    }

    function handleParticipantJoined(payload?: ParticipantJoinedEvent) {
      if (!payload?.id) {
        return;
      }
      useCallModerationState.getState().upsertParticipant(
        toModerationParticipant(
          {
            participantId: payload.id,
            displayName: payload.displayName,
            email: payload.email,
          },
          { isLocal: false },
        ),
      );
    }

    function handleParticipantLeft(payload?: ParticipantLeftEvent) {
      if (!payload?.id) {
        return;
      }
      useCallModerationState.getState().removeParticipant(payload.id);
    }

    function handleParticipantRoleChanged(
      payload?: ParticipantRoleChangedEvent,
    ) {
      if (!payload?.id || !payload?.role) {
        return;
      }
      useCallModerationState
        .getState()
        .setParticipantRole(payload.id, payload.role === JITSI_MODERATOR_ROLE);
    }

    function handleModerationStatusChanged(
      payload?: ModerationStatusChangedEvent,
    ) {
      if (payload?.mediaType !== "audio" && payload?.mediaType !== "video") {
        return;
      }
      if (typeof payload.enabled !== "boolean") {
        return;
      }
      useCallModerationState
        .getState()
        .setModerationOn(payload.mediaType, payload.enabled);
    }

    // Breakout rooms: mirror the iframe's `breakoutRoomsUpdated` event into the store. The event
    // name is the camelCase PUBLIC name (external_api.js events map: "breakout-rooms-updated" →
    // "breakoutRoomsUpdated"), matching the sibling listeners. Payload is `{ rooms: <map> }` —
    // shape verified against stable/jitsi-meet_11031 — normalized (still defensively) by
    // `toBreakoutRooms`; see its doc comment for the full verified contract.
    function handleBreakoutRoomsUpdated(payload?: unknown) {
      useCallModerationState
        .getState()
        .setBreakoutRooms(toBreakoutRooms(payload));
    }

    // `moderationParticipantApproved`/`Rejected` payload shapes are unconfirmed (design doc §3/§7).
    // Roster membership/role is already kept in sync via participantJoined/Left/RoleChanged above,
    // so these are intentional no-ops reserved for future UX (e.g. a toast) rather than left
    // unhandled. Subscribed under their correct camelCase public names for accuracy — only
    // `moderationStatusChanged` below is load-bearing.
    function noop() {}

    // Event names MUST be the camelCase PUBLIC names the external API emits. The external_api.js
    // events map is internal-hyphen → camelCase-public (e.g. "moderation-status-changed" →
    // "moderationStatusChanged") and the transport handler re-emits the camelCase value. Subscribing
    // with the internal hyphenated names (the previous bug) means the listener never fires — which
    // left `moderationOn` unsynced and the Require-approval switches stuck (BUG #1). Verified against
    // the deployed build v=9268; the sibling listeners above already used the correct camelCase form.
    apiRef.on("videoConferenceJoined", handleVideoConferenceJoined);
    apiRef.on("participantJoined", handleParticipantJoined);
    apiRef.on("participantLeft", handleParticipantLeft);
    apiRef.on("participantRoleChanged", handleParticipantRoleChanged);
    apiRef.on("moderationStatusChanged", handleModerationStatusChanged);
    apiRef.on("moderationParticipantApproved", noop);
    apiRef.on("moderationParticipantRejected", noop);
    apiRef.on("breakoutRoomsUpdated", handleBreakoutRoomsUpdated);

    return () => {
      apiRef.removeListener(
        "videoConferenceJoined",
        handleVideoConferenceJoined,
      );
      apiRef.removeListener("participantJoined", handleParticipantJoined);
      apiRef.removeListener("participantLeft", handleParticipantLeft);
      apiRef.removeListener(
        "participantRoleChanged",
        handleParticipantRoleChanged,
      );
      apiRef.removeListener(
        "moderationStatusChanged",
        handleModerationStatusChanged,
      );
      apiRef.removeListener("moderationParticipantApproved", noop);
      apiRef.removeListener("moderationParticipantRejected", noop);
      apiRef.removeListener("breakoutRoomsUpdated", handleBreakoutRoomsUpdated);
      useCallModerationState.getState().resetModerationState();
    };
  }, [apiRef]);
}
